import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  AccessTime as AccessTimeIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSocketContext } from "../../../context/SocketContext";
import { useSelector } from "react-redux";
import { MEDIA_URL, getImageDisplayUrl } from "src/network/endpoints";
import moment from "moment";

// Styled components for the sub-conversation info dialog
const Backdrop = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const DialogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 12,
  boxShadow:
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  maxWidth: 400,
  width: "90%",
  maxHeight: "80vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const Header = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#f9fafb",
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: "20px",
}));

const ParticipantItem = styled(ListItem)(({ theme }) => ({
  padding: "12px 0",
  borderBottom: "1px solid #f3f4f6",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const SubConversationInfo = ({
  isOpen,
  onClose,
  subConversationId,
  subConversationData,
}) => {
  const { emit, addEventListener, isConnected, connectionStatus } =
    useSocketContext();
  const user = useSelector((state) => state.auth.user);
  const [subConversationInfo, setSubConversationInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const subConversationDataRef = useRef(subConversationData);
  const timeoutRef = useRef(null);

  // Update ref when subConversationData changes
  useEffect(() => {
    subConversationDataRef.current = subConversationData;
  }, [subConversationData]);

  const fetchSubConversationInfo = useCallback(() => {
    if (!subConversationId) {
      setError("No sub-conversation ID provided");
      setLoading(false);
      return;
    }

    if (!emit) {
      setError("Socket connection not available");
      setLoading(false);
      return;
    }

    if (!isConnected) {
      setError("Socket not connected. Please check your connection.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = emit("getSubConversationInfo", { subConversationId });

      if (!result) {
        setError("Failed to send request - socket not connected");
        setLoading(false);
      }
    } catch (error) {
      setError("Failed to send request: " + error.message);
      setLoading(false);
    }
  }, [subConversationId, emit, isConnected, connectionStatus]);

  // Get sub-conversation info when component opens
  useEffect(() => {
    if (isOpen && subConversationId) {
      setHasReceivedData(false); // Reset flag when opening
      fetchSubConversationInfo();

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Set a timeout to show fallback data if no response
      timeoutRef.current = setTimeout(() => {
        // Only use fallback if we haven't received data from socket
        if (!hasReceivedData) {
          const currentData = subConversationDataRef.current;
          if (currentData) {
            setSubConversationInfo(currentData);
            setLoading(false);
          } else {
            setError("Unable to fetch sub-conversation details");
            setLoading(false);
          }
        } else {
        }
        timeoutRef.current = null;
      }, 5000); // 5 second timeout

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    } else {
    }
  }, [isOpen, subConversationId, fetchSubConversationInfo]);

  // Listen for sub-conversation info response
  useEffect(() => {
    const unsubscribe = addEventListener("subConversationInfo", (data) => {
      // Clear timeout since we received data
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (data && data.subConversationInfo) {
        setSubConversationInfo(data.subConversationInfo);
        setLoading(false);
        setError(null);
        setHasReceivedData(true); // Mark that we received data
      } else {
        setError("Invalid response from server");
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [addEventListener]);

  // Listen for errors
  useEffect(() => {
    const unsubscribe = addEventListener("error", (data) => {
      // Clear timeout since we received an error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (data && data.message) {
        setError(data.message);
        setLoading(false);
      } else {
        setError("An error occurred while fetching sub-conversation info");
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [addEventListener]);

  const handleClose = () => {
    // Clear timeout when closing
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setSubConversationInfo(null);
    setError(null);
    setHasReceivedData(false); // Reset the flag
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#10b981";
      case "closed":
        return "#6b7280";
      case "pending":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "active";
      case "closed":
        return "closed";
      case "pending":
        return "pending";
      default:
        return "unknown";
    }
  };

  if (!isOpen) return null;

  return (
    <Backdrop onClick={handleClose}>
      <DialogContainer
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxWidth: 400,
          maxHeight: "80vh",
        }}
      >
        {/* Header */}
        <Header>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <GroupIcon sx={{ color: "#7C3AED", fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                textTransform: "lowercase",
              }}
            >
              sub-conversation details
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: "#6b7280" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Header>

        {/* Content */}
        <Content>
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#7C3AED" }} />
            </Box>
          )}

          {error && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
              }}
            >
              <Typography variant="body2" sx={{ color: "#dc2626", mb: 2 }}>
                {error}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <IconButton
                  onClick={fetchSubConversationInfo}
                  sx={{
                    backgroundColor: "#7C3AED",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#6d28d9",
                    },
                  }}
                >
                  retry
                </IconButton>
                {subConversationDataRef.current && (
                  <IconButton
                    onClick={() => {
                      setSubConversationInfo(subConversationDataRef.current);
                      setError(null);
                    }}
                    sx={{
                      backgroundColor: "#6b7280",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#4b5563",
                      },
                    }}
                  >
                    show basic info
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {subConversationInfo && !loading && (
            <Box>
              {/* Sub-conversation Basic Info */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: "#7C3AED",
                      fontSize: "1.25rem",
                    }}
                  >
                    <CategoryIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1f2937",
                        textTransform: "lowercase",
                        mb: 0.5,
                      }}
                    >
                      {subConversationInfo.categoryId?.name || "no category"}
                    </Typography>
                    <Chip
                      label={getStatusText(subConversationInfo.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(
                          subConversationInfo.status
                        ),
                        color: "white",
                        fontSize: "0.75rem",
                        height: 20,
                      }}
                    />
                  </Box>
                </Box>

                {/* Category Description */}
                {subConversationInfo.categoryId?.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      mb: 2,
                      fontStyle: "italic",
                    }}
                  >
                    {subConversationInfo.categoryId.description}
                  </Typography>
                )}

                {/* Main Conversation Info */}
                {subConversationInfo.mainConversation && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      main conversation:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1f2937",
                        textTransform: "lowercase",
                      }}
                    >
                      {subConversationInfo.mainConversation.groupName ||
                        "no name"}
                    </Typography>
                  </Box>
                )}

                {/* Timestamps */}
                <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      created:{" "}
                      {moment(subConversationInfo.createdAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </Typography>
                  </Box>
                </Box>

                {subConversationInfo.updatedAt && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      updated:{" "}
                      {moment(subConversationInfo.updatedAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Participants */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#1f2937",
                    mb: 2,
                    textTransform: "lowercase",
                  }}
                >
                  participants ({subConversationInfo.participants?.length || 0})
                </Typography>

                <List sx={{ p: 0 }}>
                  {subConversationInfo.participants?.map(
                    (participant, index) => {
                      const user = participant?.userId;
                      if (!user) return null; // Skip participants with null userId
                      
                      return (
                        <ParticipantItem key={participant._id || index}>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 2 }}
                          >
                            <Avatar
                              src={
                                user.image?.profilePic
                                  ? getImageDisplayUrl(user.image.profilePic, MEDIA_URL)
                                  : undefined
                              }
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: "#7C3AED",
                              }}
                            >
                              {user.fullName?.charAt(0)?.toUpperCase() || user.userName?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: "#1f2937",
                                  textTransform: "lowercase",
                                }}
                              >
                                {user.fullName || user.userName || "unknown user"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280" }}
                              >
                                {user.userName || "no username"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              {/* User Type Badge */}
                              <Chip
                                label={participant.userType || "user"}
                                size="small"
                                sx={{
                                  backgroundColor: 
                                    participant.userType === "owner" ? "#10B981" :
                                    participant.userType === "teamMember" ? "#3B82F6" :
                                    "#6B7280",
                                  color: "white",
                                  fontSize: "0.7rem",
                                  height: 18,
                                }}
                              />
                              {user.isOnline && (
                                <Chip
                                  label="online"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    fontSize: "0.7rem",
                                    height: 18,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </ParticipantItem>
                      );
                    }
                  )}
                </List>
              </Box>

              {/* Last Message */}
              {subConversationInfo.lastMessage && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#1f2937",
                        mb: 1,
                        textTransform: "lowercase",
                      }}
                    >
                      last message
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#f9fafb",
                        padding: 2,
                        borderRadius: 1,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1f2937",
                          mb: 1,
                        }}
                      >
                        {subConversationInfo.lastMessage.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        {moment(
                          subConversationInfo.lastMessage.createdAt
                        ).format("DD/MM/YYYY HH:mm")}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Content>
      </DialogContainer>
    </Backdrop>
  );
};

export default SubConversationInfo;
